import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { useApolloClient, useMutation } from '@apollo/client';
import FloatingButton from '@fuww/library/src/Button/FloatingButton';
import { useSiteContext } from '../../SiteContext';
import { Link } from '../../../routes.mjs';
import addUTMSource from '../../../lib/addUtmSource';
import getJobEventTracker from '../../../lib/getJobEventTracker';
import messages from '../../../lib/messages.json';
import increaseJobApplyButtonClicks
  from '../../../lib/queries/jobs/increaseJobApplyButtonClicksMutation.graphql';
import { Job } from '../../../lib/graphql/api/graphql';

const ApplyButton = ({ job }: {
  job?: Pick<Job, 'applyForm' | 'applyUrl' | 'id'> | null;
}) => {
  const { locale, isCareerPage } = useSiteContext();
  const { query: { slug, companySlug } } = useRouter();
  const intl = useIntl();
  const [increaseApplyButtonClicks] = useMutation(increaseJobApplyButtonClicks);
  const apolloClient = useApolloClient();

  const {
    applyForm,
    applyUrl,
    id,
  } = { ...job };

  const applyButtonTracker = applyForm
    ? getJobEventTracker(
      apolloClient,
      'Meteor Job universal apply button',
      job,
    )
    : getJobEventTracker(
      apolloClient,
      'Meteor Job apply button',
      job,
    );

  const button = (
    <FloatingButton
      component="a"
      variant="extended"
      color="primary"
      onClick={() => {
        increaseApplyButtonClicks({
          variables: {
            id,
          },
        });
        applyButtonTracker();
      }}
      target={applyForm ? null : '_blank'}
      rel={applyForm ? undefined : 'noopener noreferrer'}
      href={applyUrl ? addUTMSource(applyUrl) : undefined}
    >
      {intl.formatMessage(messages.applyButtonText)}
    </FloatingButton>
  );

  return applyForm ? (
    <Link
      {...{
        route: isCareerPage
          ? `careers-job-apply.${locale}`
          : `job-apply.${locale}`,
        params: {
          id: String(id),
          slug,
          ...isCareerPage ? { companySlug } : {},
        },
      }}
      passHref
    >
      {button}
    </Link>
  ) : button;
};

ApplyButton.propTypes = {
  job: PropTypes.shape({
    applyForm: PropTypes.bool,
    applyUrl: PropTypes.string,
    id: PropTypes.number,
  }).isRequired,
};

export default ApplyButton;
